<template>
  <div ref="containerRef" class="settings-component">
    <form @submit.prevent="saveCompanyData">
      <div class="mb-3">
        <label for="address" class="form-label">{{
          $t("company.address")
        }}</label>
        <input
          type="text"
          class="form-control"
          id="address"
          v-model="companyData.address"
          maxlength="256"
        />
      </div>
      <div class="mb-3">
        <label for="vat" class="form-label">{{ $t("company.vat") }}</label>
        <input
          type="text"
          class="form-control"
          :class="{ 'is-invalid': v$.vat.$error }"
          id="vat"
          v-model="companyData.vat"
          @blur="handleVatBlur"
        />
        <div v-if="v$.vat.$error" class="invalid-feedback">
          {{ v$.vat.$errors[0].$message }}
        </div>
      </div>
      <div class="mb-3">
        <label for="bank_account" class="form-label">{{
          $t("company.bank_account")
        }}</label>
        <input
          type="text"
          class="form-control"
          :class="{ 'is-invalid': v$.bank_account.$error }"
          id="bank_account"
          v-model="companyData.bank_account"
          @blur="handleIbanBlur"
          :disabled="isValidating"
        />
        <div v-if="v$.bank_account.$error" class="invalid-feedback">
          {{ v$.bank_account.$errors[0].$message }}
        </div>
        <div class="form-text" v-if="isValidating">
          {{ $t("common.validating") }}...
        </div>
      </div>
      <div class="mb-3">
        <label for="bank_name" class="form-label">{{
          $t("company.bank_name")
        }}</label>
        <input
          type="text"
          class="form-control"
          id="bank_name"
          v-model="companyData.bank_name"
          disabled
        />
      </div>
      <div class="mb-3">
        <label for="bank_swift" class="form-label">{{
          $t("company.bank_swift")
        }}</label>
        <input
          type="text"
          class="form-control"
          id="bank_swift"
          v-model="companyData.bank_swift"
          disabled
        />
      </div>
      <div class="mb-3">
        <label :for="payoutCurrencyId" class="form-label">{{
          $t("company.payout_currency")
        }}</label>
        <select
          :id="payoutCurrencyId"
          v-model="companyData.payout_currency"
          data-test="payout-currency"
          class="form-select"
          :disabled="!canChoosePayoutCurrency"
        >
          <option
            v-for="currency in payoutCurrencyOptions"
            :key="currency"
            :value="currency"
          >
            {{ currency }}
          </option>
        </select>
        <div v-if="!canChoosePayoutCurrency" class="form-text">
          {{ $t("company.payout_currency_hint") }}
        </div>
      </div>
      <div class="mb-3">
        <label for="invoice_email" class="form-label">
          {{ $t("company.billing_email") }}
          <span
            class="ms-1 text-muted"
            data-bs-toggle="tooltip"
            :title="$t('company.billing_email_tooltip')"
          >
            <FontAwesomeIcon icon="circle-info" />
          </span>
        </label>
        <input
          type="email"
          class="form-control"
          :class="{ 'is-invalid': v$.invoice_email.$error }"
          id="invoice_email"
          v-model="companyData.invoice_email"
          @blur="v$.invoice_email.$touch()"
          placeholder="finance@company.com"
        />
        <div v-if="v$.invoice_email.$error" class="invalid-feedback">
          {{ v$.invoice_email.$errors[0].$message }}
        </div>
      </div>
      <div class="mb-3 form-check form-switch">
        <input
          type="checkbox"
          class="form-check-input"
          id="monthly_rollup"
          v-model="companyData.monthly_notification_rollup"
          role="switch"
        />
        <label class="form-check-label" for="monthly_rollup">
          {{ $t("company.monthly_rollup") }}
          <span
            class="ms-1 text-muted"
            data-bs-toggle="tooltip"
            :title="$t('company.monthly_rollup_tooltip')"
          >
            <FontAwesomeIcon icon="circle-info" />
          </span>
        </label>
      </div>
      <button type="submit" class="btn btn-primary" :disabled="!isFormValid">
        <span v-if="isSaving">{{ $t("common.saving") }}...</span>
        <span v-else>{{ $t("common.save") }}</span>
      </button>
    </form>
    <br />
    <TeamComponent :company_id="companyId" :company="company" />
  </div>
</template>

<script setup lang="ts">
import { useAccountStore } from "@/composables/account/useAccountStore";
import { normalizeVat } from "@/composables/useValidators";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useServerDraft } from "@/composables/useServerDraft";
import { useValidators } from "@/composables/useValidators";
import {
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
} from "@/money/currency_constants";
import type { Company } from "@/types";
import { useVuelidate } from "@vuelidate/core";
import { Tooltip } from "bootstrap";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
} from "vue";
import { useI18n } from "vue-i18n";
import TeamComponent from "./TeamComponent.vue";

const props = defineProps<{
  companyId: number;
  company: Company;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const validators = useValidators(t);
const accountStore = useAccountStore();

const containerRef = useTemplateRef<HTMLElement>("containerRef");
let tooltipInstances: Tooltip[] = [];

const cleanupTooltips = () => {
  tooltipInstances.forEach((tooltip) => tooltip.dispose());
  tooltipInstances = [];
};

onMounted(() => {
  const el = containerRef.value;
  if (!el) return;
  el.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
    tooltipInstances.push(new Tooltip(element));
  });
});

onBeforeUnmount(() => {
  cleanupTooltips();
});

const payoutCurrencyId = useId();

// The backend refuses a non-EUR payout currency without ledger_reads; offer only what it accepts.
const canChoosePayoutCurrency = computed(() =>
  Boolean(props.company.feature_flags?.ledger_reads)
);
const payoutCurrencyOptions = computed(() =>
  canChoosePayoutCurrency.value ? SUPPORTED_CURRENCIES : [DEFAULT_CURRENCY]
);

const companyForm = (company: Company) => ({
  address: company.address ?? "",
  country: company.country ?? "",
  vat: company.vat ?? "",
  bank_account: company.bank_account ?? "",
  bank_name: company.bank_name ?? "",
  bank_swift: company.bank_swift ?? "",
  payout_currency: company.payout_currency ?? DEFAULT_CURRENCY,
  invoice_email: company.invoice_email ?? "",
  monthly_notification_rollup: company.monthly_notification_rollup ?? false,
});

const isValidating = ref(false);
const isSaving = ref(false);
const externalResults = ref<Record<string, string[]>>({
  bank_account: [],
});

const companyData = useServerDraft(
  () => ({ companyId: props.companyId, form: companyForm(props.company) }),
  ({ form }) => form,
  {
    onReset: () => {
      externalResults.value = { bank_account: [] };
      v$.value.$reset();
    },
  }
);

const rules = {
  vat: { isValidVAT: validators.isValidVAT },
  bank_account: { iban: validators.iban },
  invoice_email: { email: validators.email },
};

const v$ = useVuelidate(rules, companyData, {
  $externalResults: externalResults,
  $scope: "settings",
});

const isFormValid = computed(
  () => !v$.value.$invalid && !isValidating.value && !isSaving.value
);

const handleIbanBlur = async () => {
  externalResults.value = { bank_account: [] };
  companyData.value.bank_name = "";
  companyData.value.bank_swift = "";
  v$.value.bank_account.$touch();
  if (v$.value.bank_account.$error) return;

  const iban = companyData.value.bank_account;
  if (iban) {
    isValidating.value = true;
    try {
      const response = await goSolidApi.get(
        `/api/utils/iban-validator/${iban}`
      );
      // The form may have started over from the server's values meanwhile: the bank belongs to this
      // IBAN only, and must not be saved beside another.
      if (companyData.value.bank_account !== iban) return;
      if (response?.status === 200 && response.data?.valid) {
        companyData.value.bank_name = response.data.bank_name;
        companyData.value.bank_swift = response.data.swift_code;
      } else {
        externalResults.value = { bank_account: [t("validations.iban")] };
      }
    } catch (error) {
      if (companyData.value.bank_account === iban) {
        externalResults.value = { bank_account: [t("validations.iban")] };
      }
      globalErrorHandler(error);
    } finally {
      isValidating.value = false;
    }
  }
};

const normalizeCompanyVat = () => {
  companyData.value.vat =
    normalizeVat(companyData.value.vat, companyData.value.country) ?? "";
};

const handleVatBlur = () => {
  normalizeCompanyVat();
  v$.value.vat.$touch();
};

const saveCompanyData = async () => {
  normalizeCompanyVat();
  v$.value.$touch();
  if (!isFormValid.value) return;

  isSaving.value = true;
  try {
    const updatedCompany = {
      ...props.company,
      address: companyData.value.address || null,
      vat: companyData.value.vat,
      bank_account: companyData.value.bank_account,
      bank_name: companyData.value.bank_name,
      bank_swift: companyData.value.bank_swift,
      payout_currency: companyData.value.payout_currency,
      invoice_email: companyData.value.invoice_email || null,
      monthly_notification_rollup:
        companyData.value.monthly_notification_rollup,
    };
    await accountStore.updateCompany(updatedCompany);
    globalSuccessHandler(t("common.saved"), " ");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSaving.value = false;
  }
};
</script>
